import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { useDebounce } from '@repo/toolkit/use-debounce'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Box from '@repo/ui/box'
import Button from '@repo/ui/button'
import DraggableList from '@repo/ui/draggable-list'
import { ChevronLeftIcon, PlusIcon } from '@repo/ui/icons'
import Loader from '@repo/ui/loader'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { toast } from 'sonner'

import { createLanguageTestSection } from '../../api/language-test/create-language-test-section'
import { getLanguageTestTemplate } from '../../api/language-test/get-language-test-template'
import { updateLanguageTestSection } from '../../api/language-test/update-language-test-section'
import { updateSectionPositions } from '../../api/language-test/update-section-positions'
import UserNavBar from '../../components/elements/nav-bar'
import LanguageTestHeader from '../../components/pages/language-test/language-test-header'
import LanguageTestSectionCard from '../../components/pages/language-test/language-test-section-card'
import LanguageTestSectionCreateCard from '../../components/pages/language-test/language-test-section-create-card'
import { Paths } from '../../lib/constants/path'
import { LanguageTestSection } from '../../lib/types/models/language-test/language-test-section'
import { LanguageTestTemplate } from '../../lib/types/models/language-test/language-test-template'
import { showAxiosError } from '../../lib/utils/functions/misc.functions'
import { queryClient } from '../../main'

type ExtendedSection = Omit<LanguageTestSection, 'id'> & {
  id?: number
  isTemporary?: boolean
  isEditing?: boolean
}

const LanguageTestDetails = () => {
  const { id: templateId } = useParams<{ id: string }>()
  const { navigate } = useNavigator()
  const location = useLocation()

  const [isAddingSection, setIsAddingSection] = useState(false)
  const [isUpdatingPositions, setIsUpdatingPositions] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [editingSectionId, setEditingSectionId] = useState<number | null>(null)

  // Getting the template from shared state
  // This will help prevent showing the user a loading state and make the transition smoother
  const template = location.state?.template as LanguageTestTemplate | undefined

  const { data: languageTestTemplateData, isLoading } = useQuery({
    queryKey: ['language-test-template', templateId],
    queryFn: () => getLanguageTestTemplate(parseInt(templateId!)),
    select: (data) => ({
      ...data,
      sections: data.sections.sort((a, b) => a.position - b.position)
    }),
    enabled: !!templateId && !isNaN(parseInt(templateId))
  })

  const languageTestTemplate = useMemo(() => {
    return languageTestTemplateData ?? template
  }, [languageTestTemplateData, template])

  const createSectionMutation = useMutation({
    mutationFn: async (sectionLabel: string) => {
      if (!languageTestTemplate) return
      const maxPosition = Math.max(
        0,
        ...(languageTestTemplate.sections?.map((s) => s.position) || [])
      )

      return createLanguageTestSection({
        templateId: languageTestTemplate.id,
        label: sectionLabel,
        position: maxPosition + 1
      })
    },
    onSuccess: () => {
      setIsAddingSection(false)
      queryClient.invalidateQueries({ queryKey: ['language-test-template', templateId] })
      toast.success('Section créée avec succès')
    },
    onError: showAxiosError
  })

  const updateSectionMutation = useMutation({
    mutationFn: updateLanguageTestSection,
    onSuccess: () => {
      setEditingSectionId(null)
      queryClient.invalidateQueries({ queryKey: ['language-test-template', templateId] })
      toast.success('Section mise à jour avec succès')
    },
    onError: showAxiosError
  })

  const { mutate: updatePositionsMutation } = useMutation({
    mutationFn: updateSectionPositions,
    onMutate: () => {
      setIsUpdatingPositions(true)
    },
    onSuccess: () => {
      setIsUpdatingPositions(false)
      queryClient.invalidateQueries({ queryKey: ['language-test-template', templateId] })
      toast.success('Positions des sections mises à jour avec succès')
    },
    onError: (error) => {
      setIsUpdatingPositions(false)
      showAxiosError(error)
    }
  })

  const handleUpdateSectionPositions = useCallback(
    (sections: LanguageTestSection[]) => {
      if (!languageTestTemplate?.id) return

      const sectionPositions = sections.map((section, index) => ({
        sectionId: section.id,
        position: index + 1
      }))

      updatePositionsMutation({
        id: languageTestTemplate.id,
        sectionPositions
      })
    },
    [languageTestTemplate?.id, updatePositionsMutation]
  )

  // Debounced function with cancellation support
  const { debouncedCallback: debouncedUpdatePositions, cancel: cancelUpdatePositions } =
    useDebounce(handleUpdateSectionPositions, 1500)

  // Store pending sections for delayed update
  const pendingSectionsRef = useRef<LanguageTestSection[] | null>(null)

  // Effect to handle updates when dragging stops
  useEffect(() => {
    if (!isDragging && pendingSectionsRef.current) {
      debouncedUpdatePositions(pendingSectionsRef.current)
      pendingSectionsRef.current = null
    }
  }, [isDragging, debouncedUpdatePositions])

  // Create extended sections list that includes the temporary "add new" item
  const extendedSections = useMemo(() => {
    const sections: ExtendedSection[] = [...(languageTestTemplate?.sections || [])].map(
      (section) => ({
        ...section,
        isEditing: section.id === editingSectionId
      })
    )

    if (isAddingSection) {
      sections.push({
        label: '',
        position: sections.length + 1,
        templateId: languageTestTemplate?.id || 0,
        questions: [],
        isTemporary: true
      })
    }

    return sections
  }, [languageTestTemplate?.sections, isAddingSection, editingSectionId])

  const transformToDraggableItems = (sections: ExtendedSection[]) => {
    return sections.map((section) => ({
      ...section,
      id: section.id ?? -1 // had to unlucde id here for draggable list to work
    }))
  }

  const handleSectionReorder = (reorderedItems: ExtendedSection[]) => {
    const realSections = reorderedItems.filter(
      (s) => !s.isTemporary && typeof s.id === 'number'
    ) as LanguageTestSection[]
    pendingSectionsRef.current = realSections
  }

  const handleDragStart = () => {
    setIsDragging(true)
    cancelUpdatePositions()
  }

  const handleDragEnd = () => setIsDragging(false)

  const handleSectionClick = (section: LanguageTestSection) => {
    if (!templateId) return

    navigate(
      Paths.LANGUAGE_TEST_TEMPLATE_SECTION_DETAILS_PAGE.replace(
        ':templateId',
        templateId.toString()
      ).replace(':sectionId', section.id.toString())
    )
  }

  const handleEditSection = (section: LanguageTestSection) => {
    setEditingSectionId(section.id)
  }

  const handleUpdateSection = (sectionId: number, newLabel: string) => {
    updateSectionMutation.mutate({
      id: sectionId,
      label: newLabel
    })
  }

  const handleCancelEdit = () => {
    setEditingSectionId(null)
  }

  const handleAddSection = () => setIsAddingSection(true)
  const handleCancelNewSection = () => setIsAddingSection(false)

  const handleConfirmNewSection = (sectionLabel: string) => {
    createSectionMutation.mutate(sectionLabel)
  }

  if (isLoading && !template) {
    return (
      <div>
        <UserNavBar
          title={'Test de langues'}
          icon={ChevronLeftIcon}
          onIconClick={() => navigate(Paths.LANGUAGE_TEST_TEMPLATE_LIST_PAGE)}
        />
        <div className="h-4" />
        <Box className="p-4">
          <div className="flex justify-center items-center min-h-56">
            <Loader />
          </div>
        </Box>
      </div>
    )
  }

  if (!languageTestTemplate) {
    return (
      <div>
        <UserNavBar
          title={'Test de langues'}
          icon={ChevronLeftIcon}
          onIconClick={() => navigate(Paths.LANGUAGE_TEST_TEMPLATE_LIST_PAGE)}
        />
        <div className="h-4" />
        <Box className="p-4">
          <div className="flex flex-col items-center justify-center gap-4 min-h-56">
            <Typography variant="sub-heading">Test de langue non trouvé.</Typography>
            <Button
              size="small"
              variant="primary"
              icon={ChevronLeftIcon}
              onClick={() => navigate(Paths.LANGUAGE_TEST_TEMPLATE_LIST_PAGE)}
              label="Retour à la liste des tests de langue"
            />
          </div>
        </Box>
      </div>
    )
  }

  return (
    <div>
      <UserNavBar
        title={'Test de langues'}
        icon={ChevronLeftIcon}
        onIconClick={() => navigate(Paths.LANGUAGE_TEST_TEMPLATE_LIST_PAGE)}
      />
      <div className="h-4" />

      <Box className="p-4">
        <LanguageTestHeader
          template={languageTestTemplate}
          onDeleteSuccess={() => navigate(Paths.LANGUAGE_TEST_TEMPLATE_LIST_PAGE)}
        />

        <div className="h-4" />

        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Typography variant="h5">Sections du test</Typography>
              {isUpdatingPositions && !isDragging && (
                <div className="flex items-center gap-1.5 px-2 py-1 bg-blue-50 border border-grey-light-hover rounded-full">
                  <Loader size="xs" color={tailwindColors.secondary.hover} />
                  <Typography variant="small">Enregistrement des positions...</Typography>
                </div>
              )}
            </div>
            <Button
              icon={PlusIcon}
              label="Ajouter une section"
              onClick={handleAddSection}
              disabled={
                isAddingSection || createSectionMutation.isPending || editingSectionId !== null
              }
            />
          </div>

          {extendedSections && extendedSections.length > 0 ? (
            <DraggableList
              items={transformToDraggableItems(extendedSections)}
              onReorder={handleSectionReorder}
              disabled={isAddingSection || editingSectionId !== null}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              renderItem={(section, isDragging, dragHandleProps) => {
                if (section.isTemporary) {
                  return (
                    <LanguageTestSectionCreateCard
                      isDragging={isDragging}
                      dragHandleProps={dragHandleProps}
                      onConfirm={handleConfirmNewSection}
                      onCancel={handleCancelNewSection}
                      isLoading={createSectionMutation.isPending}
                    />
                  )
                }

                return (
                  <LanguageTestSectionCard
                    section={section as LanguageTestSection}
                    isDragging={isDragging}
                    dragHandleProps={dragHandleProps}
                    isEditing={section.isEditing}
                    onClick={handleSectionClick}
                    onEdit={handleEditSection}
                    onUpdate={handleUpdateSection}
                    onCancelEdit={handleCancelEdit}
                    isUpdating={updateSectionMutation.isPending}
                  />
                )
              }}
              className="gap-3"
            />
          ) : (
            <div className="flex items-center justify-center p-4">
              <Typography variant="secondary-body" color={tailwindColors.grey.light.active}>
                Aucune section trouvée
              </Typography>
            </div>
          )}
        </div>
      </Box>
    </div>
  )
}

export default LanguageTestDetails
